import { z } from "zod";

/**
 * Source provenance for OMS v3 sales-order extraction. Links an extracted value
 * (header field, line-item field) — or an unmapped source region — back to its
 * exact location in the source document, derived from Extend's citation
 * metadata. One record per citation. Coordinates are normalised (0..1) against
 * the page so the viewer is resolution-independent.
 */

/** Normalised (0..1) axis-aligned bounding box within a page. */
export const ProvenanceAreaSchema = z.object({
  left: z.number(),
  top: z.number(),
  width: z.number(),
  height: z.number(),
});
export type ProvenanceArea = z.infer<typeof ProvenanceAreaSchema>;

/** Character offset range into the email/document body text. */
export const ProvenanceTextSpanSchema = z.object({
  start: z.number().int(),
  end: z.number().int(),
});
export type ProvenanceTextSpan = z.infer<typeof ProvenanceTextSpanSchema>;

/** What an extracted value maps to. */
export const ProvenanceScopeSchema = z.enum(["header", "lineItem", "unmapped"]);
export type ProvenanceScope = z.infer<typeof ProvenanceScopeSchema>;

/** A single source-location citation for an extracted field / line / region. */
export const FieldProvenanceSchema = z.object({
  id: z.string().uuid(),
  documentId: z.string().uuid(),
  scope: ProvenanceScopeSchema,
  /** Extracted field name (e.g. "total_amount", "quantity"); null for unmapped. */
  fieldName: z.string().nullable(),
  /** Stable line-item id when scope is "lineItem". */
  lineItemId: z.string().uuid().nullable(),
  /** Extraction array index (== draft line `lineNumber`) when scope is "lineItem". */
  lineNumber: z.number().int().nullable(),
  /** 1-based source page. */
  page: z.number().int().nullable(),
  area: ProvenanceAreaSchema.nullable(),
  /** Spreadsheet A1 cell ref, when derivable (not available from citations today). */
  cellRef: z.string().nullable(),
  textSpan: ProvenanceTextSpanSchema.nullable(),
  /** Hover-sync key shared between the table row and the source overlay. */
  blockId: z.string().nullable(),
  /** Extend's matched source substring (chip tooltip / flash target). */
  referenceText: z.string().nullable(),
  /** false → dashed "unmapped region" box; true → solid mapped box. */
  mapped: z.boolean(),
});
export type FieldProvenance = z.infer<typeof FieldProvenanceSchema>;

/**
 * Friendly labels for the raw Extend extraction-schema field names stored on
 * provenance rows, so the Document tab shows "Customer PO #" instead of
 * "purchase_order_number". Keys MUST match the field names produced by the OCR
 * extraction schema (`backend/.../ai/ocr/v2/ExtendAIOCRConfig.ts`); keep the two
 * in sync when the schema changes. Unmapped names fall back to a humanized form.
 */
export const PROVENANCE_FIELD_LABELS: Record<string, string> = {
  // Header fields
  purchase_order_number: "Customer PO #",
  received_date: "Received date",
  order_date: "Order date",
  delivery_date: "Delivery date",
  ship_date: "Ship date",
  shipping_terms: "Shipping terms",
  shipping_method: "Shipping method",
  freight_carrier: "Freight carrier",
  subtotal: "Subtotal",
  tax_amount: "Tax",
  tax_rate: "Tax rate",
  total_amount: "Order total",
  currency: "Currency",
  payment_terms: "Payment terms",
  payment_method: "Payment method",
  street: "Street",
  city: "City",
  state: "State",
  zip_code: "ZIP code",
  country: "Country",
  // Line-item fields
  customer_identifier: "Item #",
  customer_description: "Description",
  origin: "Country of origin",
  unit: "Unit",
  pallets: "Pallets",
  quantity: "Qty",
  unit_price: "Unit price",
  amount: "Line total",
};

/** Title-case a raw snake/dotted field name as a last-resort label. */
function humanizeFieldName(fieldName: string): string {
  const leaf = fieldName.split(".").pop() ?? fieldName;
  return leaf
    .split("_")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/**
 * Friendly display label for a provenance field name. Returns a mapped label
 * when known, a humanized fallback otherwise, and a generic term when null.
 */
export function provenanceFieldLabel(fieldName: string | null): string {
  if (!fieldName) return "Region";
  return (
    PROVENANCE_FIELD_LABELS[fieldName] ??
    PROVENANCE_FIELD_LABELS[fieldName.split(".").pop() ?? fieldName] ??
    humanizeFieldName(fieldName)
  );
}
